import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Target, ShieldCheck, Eye, Clock, CheckCircle, Sparkle,
  Lightning, GlobeHemisphereWest, Scales, PlayCircle,
} from '@phosphor-icons/react';
import { Shell } from '../components/layout/Shell';

const Landing = () => {
  return (
    <Shell>
      {/* Hero */}
      <section className="relative border-b border-border" data-testid="landing-hero">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="label-eyebrow text-primary mb-4">HIRE · WITH · CONFIDENCE</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Hire by proof,<br />
              not by <span className="line-through decoration-primary decoration-4">resume</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-xl sm:text-2xl leading-snug tracking-tight font-medium text-foreground">
              Résumés died the day AI agents learned to write them.
              <span className="text-muted-foreground"> Hikari is how you hire in the post-résumé world.</span>
            </p>
            <p className="mt-6 max-w-2xl text-base text-muted-foreground leading-relaxed">
              Candidates show their capability through structured assessments and real work.
              Employers see a transparent, ranked shortlist — with every score explained,
              every flag surfaced, every gaming attempt caught.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/engine">
                <Button size="lg" className="rounded-none h-12 px-6" data-testid="cta-see-engine">
                  <PlayCircle size={18} weight="fill" className="mr-2" /> See the engine live
                </Button>
              </Link>
              <Link to="/register?role=candidate">
                <Button size="lg" variant="outline" className="rounded-none h-12 px-6" data-testid="cta-candidate">
                  I'm looking for work <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/register?role=employer">
                <Button size="lg" variant="outline" className="rounded-none h-12 px-6" data-testid="cta-employer">
                  I'm hiring <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/sandbox">
                <Button size="lg" variant="ghost" className="rounded-none h-12 px-6 text-primary hover:bg-primary/10" data-testid="cta-sandbox">
                  Try the 60-sec sandbox →
                </Button>
              </Link>
            </div>
            {/* 3-minute narrated walkthrough — TTS via OpenAI on Universal key */}
            <div className="mt-8 max-w-2xl border border-border bg-white p-4" data-testid="landing-audio">
              <div className="flex items-center gap-2 mb-2">
                <PlayCircle size={16} weight="fill" className="text-primary" />
                <div className="label-eyebrow">
                  LISTEN · 3-MIN WALKTHROUGH
                </div>
              </div>
              <p className="mono text-[11px] text-muted-foreground mb-3 leading-relaxed">
                Hate reading? Press play. We'll walk you through the whole engine in three minutes — built proof, ranked candidates, transparent scoring, the lot.
              </p>
              <audio
                src="/hikari_walkthrough.mp3"
                controls
                preload="metadata"
                className="w-full"
                data-testid="landing-audio-player"
              >
                Your browser doesn't support audio playback.
              </audio>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /> Hire with confidence · worldwide</span>
              <span className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /> Transparent scoring</span>
              <span className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /> Anti-gaming signals</span>
            </div>
          </div>
          <div className="lg:col-span-4 relative">
            <div className="sticky top-28 border border-border bg-white p-6">
              <div className="label-eyebrow text-muted-foreground mb-4">SAMPLE CANDIDATE SCORE</div>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="mono font-black text-6xl text-primary">87</span>
                <span className="mono text-sm text-muted-foreground">/100 fit</span>
              </div>
              <div className="space-y-3">
                {[
                  { k: 'Performance', v: 91 },
                  { k: 'Consistency', v: 88 },
                  { k: 'Reliability', v: 95 },
                  { k: 'Artifacts', v: 72 },
                ].map((i) => (
                  <div key={i.k}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="label-eyebrow text-muted-foreground">{i.k}</span>
                      <span className="mono font-bold">{i.v}</span>
                    </div>
                    <div className="score-bar"><div className="score-bar-fill" style={{ width: `${i.v}%` }} /></div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-border pt-4">
                <div className="label-eyebrow text-muted-foreground mb-2">Explanation</div>
                <p className="text-sm leading-relaxed">
                  Strong performance on admin fundamentals with near-identical scores across three attempts.
                  Two verified work samples match the job's required skills.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The resume is dead — post-résumé world positioning */}
      <section className="border-b border-border bg-foreground text-background" data-testid="resume-dead-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="label-eyebrow text-accent mb-4">THE POST-RÉSUMÉ WORLD</div>
              <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                Résumés died the day AI agents learned to write them.
              </h2>
              <p className="mt-8 text-lg text-background/80 leading-relaxed max-w-2xl">
                In 2026, an AI agent can tweak a résumé for 500 postings in a single afternoon.
                Every applicant now looks equally qualified on paper.
                The résumé, as a signal, is over.
              </p>
              <p className="mt-4 text-lg text-background/80 leading-relaxed max-w-2xl">
                Hikari is the filter that still works. Candidates <em>do</em> the work —
                timed assessments they can't fake, consistency across attempts, real artifacts.
                Scores are generated by an independent system that flags speed-gaming,
                duplicate answers, and AI-written responses.
              </p>
              <p className="mt-6 font-display text-2xl lg:text-3xl tracking-tight leading-tight max-w-2xl">
                "Don't send me a résumé. Send me your <span className="text-accent">Hikari score</span>."
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-background/20 p-6">
                <div className="label-eyebrow text-accent mb-4">WHAT AN AI AGENT CAN'T FAKE</div>
                <ul className="space-y-4 text-sm">
                  <li className="flex gap-3">
                    <span className="mono text-accent shrink-0">01</span>
                    <div>
                      <strong>Sitting down and taking timed assessments.</strong>
                      <div className="text-background/70">Our tests are rate-limited, time-boxed, and calibrated to the specific job family.</div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-accent shrink-0">02</span>
                    <div>
                      <strong>Consistency across repeated attempts.</strong>
                      <div className="text-background/70">Luck fades; skill repeats. Variance is a first-class scoring signal.</div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-accent shrink-0">03</span>
                    <div>
                      <strong>Anti-gaming detection.</strong>
                      <div className="text-background/70">Abnormal speed, duplicate answers, and AI-generated writing all drop the reliability sub-score visibly.</div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-accent shrink-0">04</span>
                    <div>
                      <strong>A portable score employers can independently verify.</strong>
                      <div className="text-background/70">Every candidate's result lives on a public page — not inside their cover letter.</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Paradigm comparison */}
      <section className="border-b border-border" data-testid="paradigm-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="label-eyebrow text-primary mb-4">THE SHIFT</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl max-w-3xl tracking-tighter">
            From "what you did" to "what you can do."
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-0 grid-skeleton">
            <div className="p-8 bg-secondary">
              <div className="label-eyebrow text-muted-foreground mb-4">TRADITIONAL HIRING</div>
              <ul className="space-y-3 text-base">
                <li>· Resumes ranked by keywords</li>
                <li>· References that say nothing useful</li>
                <li>· Credential bias</li>
                <li>· Opaque "gut feel" screening</li>
                <li>· Lost diamonds, hired polish</li>
              </ul>
            </div>
            <div className="p-8 bg-white">
              <div className="label-eyebrow text-primary mb-4">HIKARI HIRING</div>
              <ul className="space-y-3 text-base font-medium">
                <li>· Job-relevant structured assessments</li>
                <li>· Consistency measured across attempts</li>
                <li>· Real work samples as evidence</li>
                <li>· Transparent, explainable scoring</li>
                <li>· Anti-gaming reliability signals</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Four pillars */}
      <section className="border-b border-border" data-testid="pillars-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid md:grid-cols-4 gap-0 grid-skeleton">
            {[
              { icon: Target, title: 'Performance', body: 'Auto-graded MCQs and AI-rubric scored writing and scenarios — based on job-relevant tasks, not trivia.' },
              { icon: Clock, title: 'Consistency', body: 'Measures variance across attempts. Luck fades; skill repeats. Employers see stable capability.' },
              { icon: ShieldCheck, title: 'Reliability', body: 'Anti-gaming signals: abnormal speed, duplicate answers, and other patterns flagged transparently.' },
              { icon: Sparkle, title: 'Artifacts', body: 'Real work samples and verified credentials weighted against the job\'s required skills.' },
            ].map((p, i) => (
              <div key={i} className="p-8 bg-white hover:bg-secondary/40 transition-colors">
                <p.icon size={28} weight="duotone" className="text-primary mb-4" />
                <h3 className="font-display font-black text-xl mb-2 tracking-tight">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiation table */}
      <section className="border-b border-border bg-secondary" data-testid="competitive-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="label-eyebrow text-primary mb-4">VS THE REST</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-3xl mb-12">
            Every hiring tool claims "skills-based." Here's what's actually different about Hikari.
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[800px] bg-white" data-testid="vs-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-4 text-left label-eyebrow text-muted-foreground">CAPABILITY</th>
                  <th className="p-4 text-center label-eyebrow text-primary bg-primary/5">HIKARI</th>
                  <th className="p-4 text-center label-eyebrow text-muted-foreground">CodeSignal · HackerRank</th>
                  <th className="p-4 text-center label-eyebrow text-muted-foreground">TestGorilla · Vervoe</th>
                  <th className="p-4 text-center label-eyebrow text-muted-foreground">Traditional ATS</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ['Consistency-as-signal (variance across attempts)', '✓', '—', '—', '—'],
                  ['Proof-based hiring · works worldwide (EN/FR · Job Bank ready)', '✓', '—', '—', '—'],
                  ['Transparent scoring with AI explanations', '✓', 'partial', '—', '—'],
                  ['Built-in fairness audit', '✓', '—', 'add-on', '—'],
                  ['Admin & Ops + Customer Support focus', '✓', 'tech only', 'generic', '—'],
                  ['ML anti-gaming detector (LLM-based)', '✓', '✓', 'partial', '—'],
                  ['Portable candidate score (share anywhere)', 'coming', '—', '—', '—'],
                  ['Open scoring methodology', '✓', '—', '—', '—'],
                ].map((row, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="p-4 font-medium">{row[0]}</td>
                    <td className="p-4 text-center bg-primary/5 font-mono font-bold text-primary">{row[1]}</td>
                    <td className="p-4 text-center mono text-xs text-muted-foreground">{row[2]}</td>
                    <td className="p-4 text-center mono text-xs text-muted-foreground">{row[3]}</td>
                    <td className="p-4 text-center mono text-xs text-muted-foreground">{row[4]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* MOAT */}
      <section className="border-b border-border bg-foreground text-background" data-testid="moat-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="label-eyebrow text-accent mb-4">OUR MOAT</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-3xl mb-12">
            Six things the competition structurally cannot copy.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 grid-skeleton">
            {[
              { icon: Clock, title: 'Consistency as a signal', body: 'Variance across attempts is a genuine scoring feature — not a gimmick. Competitors grade a single moment; we grade a pattern.' },
              { icon: GlobeHemisphereWest, title: 'Hire with confidence, worldwide', body: 'Bilingual, work-authorization-aware, Job Bank + ALIS + global feeds ready. Proof-based hiring works the same in Toronto, Lagos, or Berlin.' },
              { icon: Eye, title: 'Open methodology', body: 'We publish how the engine scores. TestGorilla and CodeSignal can\'t — their rubric is proprietary IP. Transparency is the trust layer.' },
              { icon: Scales, title: 'Fairness audit built-in', body: 'Disparity metrics by country and work authorization, shipped by default. Required for Canadian regulated employers — not an enterprise upsell.' },
              { icon: Sparkle, title: 'Portable candidate proof', body: 'A Hikari score is meant to travel. Every shared link is an inbound lead. Competitors lock candidates inside one employer.' },
              { icon: Lightning, title: 'Non-technical focus', body: 'Competitors centre tech hiring. Admin, Ops, CS, skilled-trades admin — a massive underserved market with zero real standard.' },
            ].map((m, i) => (
              <div key={i} className="p-8 bg-foreground text-background">
                <m.icon size={28} weight="duotone" className="text-accent mb-4" />
                <h3 className="font-display font-black text-xl tracking-tight mb-2">{m.title}</h3>
                <p className="text-sm opacity-80 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 border border-background/30 p-6 max-w-3xl">
            <div className="label-eyebrow text-accent mb-2">THE NETWORK EFFECT</div>
            <p className="text-base leading-relaxed">
              Every candidate who completes a Hikari assessment makes the engine more accurate.
              Every employer who hires through Hikari creates pressure on the next candidate to have a
              Hikari score. The more scores in the wild, the more employers demand them — the more
              candidates complete them. A flywheel competitors can't start late.
            </p>
          </div>
        </div>
      </section>

      {/* See-the-engine banner */}
      <section className="border-b border-border bg-primary text-primary-foreground" data-testid="see-engine-banner">
        <div className="max-w-[1400px] mx-auto px-6 py-14 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="label-eyebrow mb-2 opacity-80">DON'T TAKE OUR WORD FOR IT</div>
            <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Take an assessment yourself. Watch the score build.</h3>
          </div>
          <Link to="/engine">
            <Button size="lg" className="rounded-none h-12 px-6 bg-background text-foreground hover:bg-background/90" data-testid="see-engine-btn">
              <PlayCircle size={18} weight="fill" className="mr-2" /> See the engine live <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-foreground text-background" data-testid="final-cta">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow text-accent mb-4">READY?</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
              Start with the role family that runs every company: Admin &amp; Ops Support.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/register?role=candidate">
              <Button size="lg" className="rounded-none h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="final-cta-candidate">
                Build your profile <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/register?role=employer">
              <Button size="lg" variant="outline" className="rounded-none h-12 px-6 bg-transparent border-background text-background hover:bg-background hover:text-foreground" data-testid="final-cta-employer">
                Post a job <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Shell>
  );
};

export default Landing;
